"use client";

// The dynamic transfer-status card plus the separate two-line send-rules
// checklist. The status is rendered only when useful; the rules always remain.
// The rules consolidate
// everything the user must not get wrong. Replaces `MinDepositNotice.tsx` (the
// "Send at least N" block) AND `DepositStepView`'s standalone "Only send X on Y"
// warning callout, per the 2026-07-31 "A안 · QR 퍼스트" one-page redesign:
//
//   !  Base USDT only · 6 USDT minimum
//   ↻  Reusable — deposit as many times as you like
//
// Why consolidate: the rules were spread across three surfaces (a minimum block
// above the address, a warning callout below it, and a "reusable address"
// sentence between them), each styled as its own alarm. Two short lines in one
// quiet card are read; stacked callouts are skipped.
//
// The PENDING variant adds the amber row this file inherited from
// MinDepositNotice (`.rd-min-notice-pending`): when an earlier below-minimum
// deposit is still sitting un-swept at the user's forwarder, the sweep fires on
// the COMBINED balance, so the row names both what is already waiting and what is
// still missing -- asking for the full minimum again would make the user
// over-send.
import "../styles/component.css";
import type { DepositArrivalStatus } from "../providers/relay-wizard-provider";

export interface MinNoticeData {
  min: string;
  symbol: string;
  /** An earlier below-minimum deposit still waiting un-swept at the user's
   * forwarder. `remaining` present = show the dynamic amber row. */
  pending?: string;
  pendingSymbol?: string;
  remaining?: string;
}

export function DepositStatus({
  minNotice,
  arrivalStatus,
}: {
  minNotice: MinNoticeData;
  /** SSE-derived status enriches the pending-balance message once a new
   * deposit is detected. */
  arrivalStatus: DepositArrivalStatus;
}) {
  const arrived =
    arrivalStatus.amount && arrivalStatus.symbol
      ? `${arrivalStatus.amount} ${arrivalStatus.symbol} arrived`
      : arrivalStatus.amount
        ? `${arrivalStatus.amount} arrived`
        : undefined;
  const liveMessage = (() => {
    switch (arrivalStatus.phase) {
      case "detected":
        if (minNotice.remaining !== undefined) {
          const remaining = `${minNotice.remaining} ${minNotice.symbol}`;
          return arrived
            ? `${arrived} at the processing address. Waiting for ${remaining} more to reach the minimum.`
            : `Deposit detected. Waiting for ${remaining} more to reach the minimum.`;
        }
        return arrived
          ? `${arrived} at the processing address. Checking the minimum.`
          : "Deposit detected. Checking the minimum.";
      case "funded":
        return `${minNotice.min} ${minNotice.symbol} received at the processing address. Preparing delivery.`;
      case "processing":
        return "Minimum reached. Bridging and delivering to ONEchain.";
      case "completed":
        return "Transfer completed. Funds were delivered to ONEchain.";
      case "failed":
        return `Transfer needs attention${
          arrivalStatus.backendStatus ? ` (${arrivalStatus.backendStatus})` : ""
        }.`;
      case "unavailable":
        return minNotice.remaining === undefined
          ? "Live status is temporarily unavailable. Retrying automatically."
          : undefined;
      case "connecting":
      case "waiting":
      default:
        return undefined;
    }
  })();

  return liveMessage ? (
    <div
      key={`live:${arrivalStatus.phase}:${liveMessage}`}
      className={`rd-checklist-status rd-checklist-status-${arrivalStatus.phase}`}
      data-rd-deposit-status=""
      data-phase={arrivalStatus.phase}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <span className="rd-checklist-status-dot" aria-hidden="true" />
      <span>{liveMessage}</span>
    </div>
  ) : minNotice.remaining !== undefined ? (
    <div
      key={`pending:${minNotice.remaining}:${minNotice.symbol}`}
      className="rd-checklist-status rd-checklist-status-waiting rd-min-notice-pending"
      data-rd-min-notice-pending=""
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <span className="rd-checklist-status-dot" aria-hidden="true" />
      <span>
        Waiting for{" "}
        <b>
          {minNotice.remaining} {minNotice.symbol}
        </b>{" "}
        more to arrive at the processing address.
      </span>
    </div>
  ) : null;
}

export function DepositChecklist({
  minNotice,
  onlySend,
  showWalletCreditNote = false,
}: {
  minNotice: MinNoticeData;
  /** The selected origin, for the "X on Y only" line -- the wrong-token/wrong-chain
   * rule this card absorbed from the old `.rd-address-warning` callout. */
  onlySend: { symbol: string; chainName: string };
  /** Fixed-recipient mode only: explains the hidden destination address
   * without printing the address itself. */
  showWalletCreditNote?: boolean;
}) {
  return (
    <div className="rd-checklist" data-rd-checklist="">
      <div className="rd-checklist-row" data-rd-checklist-only="">
        <span className="rd-checklist-mark rd-checklist-mark-warn" aria-hidden="true">
          !
        </span>
        <span>
          <b>
            {onlySend.chainName} {onlySend.symbol} only
          </b>
          {" · "}
          <b className="rd-checklist-accent">
            {minNotice.min} {minNotice.symbol} minimum
          </b>
        </span>
      </div>

      <div className="rd-checklist-row">
        <span className="rd-checklist-mark" aria-hidden="true">
          ↻
        </span>
        <span>Reusable — deposit as many times as you like</span>
      </div>

      {showWalletCreditNote && (
        <div className="rd-checklist-row rd-checklist-credit" data-rd-checklist-credit="">
          <span className="rd-checklist-mark" aria-hidden="true">
            →
          </span>
          <span>Credited to your ONEchain wallet</span>
        </div>
      )}
    </div>
  );
}
